import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Loader2, Wallet, X, Zap } from "lucide-react";
import FixtureTagChips from "./FixtureTagChips";
import { roomToFixtureRef } from "../../utils/bondchat/fixtureTagHelpers";
import {
  CHALLENGE_STAKES,
  CHALLENGE_ODDS,
  MIN_CHALLENGE_STAKE,
  buildChallengeMarkets,
  buildPickLabel,
  maxCounterCapacity,
} from "../../utils/bondchat/chatChallengeHelpers";

const MIN_CHALLENGE_AMOUNT = MIN_CHALLENGE_STAKE;

const StepHeading = ({ number, title, hint }) => (
  <div className="bc-challenge-step-head">
    <span className="bc-challenge-step-number">{number}</span>
    <span>
      <strong>{title}</strong>
      <small>{hint}</small>
    </span>
  </div>
);

const ChallengeDropSheet = ({ open, fixtureRoom, onClose, onDrop }) => {
  const fixtureRef = useMemo(
    () => (fixtureRoom ? roomToFixtureRef(fixtureRoom) : null),
    [fixtureRoom]
  );
  const markets = useMemo(() => buildChallengeMarkets(fixtureRef), [fixtureRef]);

  const [selectedMarketId, setSelectedMarketId] = useState(null);
  const [selectedPick, setSelectedPick] = useState(null);
  const [stance, setStance] = useState("for");
  const [budgetText, setBudgetText] = useState(String(CHALLENGE_STAKES[0]));
  const [oddsText, setOddsText] = useState(CHALLENGE_ODDS[1]);
  const [minStakeText, setMinStakeText] = useState(String(MIN_CHALLENGE_STAKE));
  const [taunt, setTaunt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedMarketId(markets[0]?.id || null);
    setSelectedPick(markets[0]?.outcomes[0]?.agreedOdds || null);
    setStance("for");
    setBudgetText(String(CHALLENGE_STAKES[0]));
    setOddsText(CHALLENGE_ODDS[1]);
    setMinStakeText(String(MIN_CHALLENGE_STAKE));
    setTaunt("");
  }, [open, fixtureRef?.fixtureId, markets]);

  const selectedMarket =
    markets.find((market) => market.id === selectedMarketId) || markets[0];

  if (!open || !fixtureRef) return null;

  const budget = Number(String(budgetText).replace(/,/g, ""));
  const offeredOdds = Number(oddsText);
  const minStake = Number(String(minStakeText).replace(/,/g, ""));
  const capacity = maxCounterCapacity(budget, offeredOdds);
  const budgetValid = Number.isFinite(budget) && budget >= MIN_CHALLENGE_AMOUNT;
  const oddsValid = Number.isFinite(offeredOdds) && offeredOdds >= 1.01 && offeredOdds <= 50;
  const minStakeValid =
    Number.isFinite(minStake) &&
    minStake >= MIN_CHALLENGE_STAKE &&
    capacity >= MIN_CHALLENGE_STAKE &&
    minStake <= capacity;
  const pickLabel = selectedPick ? buildPickLabel(fixtureRef, selectedPick) : "";
  const canSubmit =
    !!selectedPick && budgetValid && oddsValid && minStakeValid && !submitting;

  const updateBudget = (value) => {
    setBudgetText(String(value || "").replace(/[^\d]/g, "").slice(0, 9));
  };

  const updateMinStake = (value) => {
    setMinStakeText(String(value || "").replace(/[^\d]/g, "").slice(0, 9));
  };

  const isPickActive = (pick) =>
    selectedPick?.section === pick.agreedOdds.section &&
    selectedPick?.subSection === pick.agreedOdds.subSection &&
    selectedPick?.key === pick.agreedOdds.key;

  const renderOutcome = (outcome) => {
    const active = isPickActive(outcome);
    return (
      <button
        key={outcome.id}
        type="button"
        className={`bc-challenge-outcome${active ? " is-active" : ""}`}
        onClick={() => setSelectedPick(outcome.agreedOdds)}
      >
        <span>
          <strong>{outcome.label}</strong>
          <small>{selectedMarket?.subtitle}</small>
        </span>
        {active && <CheckCircle2 size={16} />}
      </button>
    );
  };

  const handleDrop = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onDrop?.({
        fixtureRef,
        fixtureId:
          fixtureRef.fixtureId?.toString?.() || String(fixtureRef.fixtureId),
        agreedOdds: selectedPick,
        stance,
        creatorBudget: budget,
        offeredOdds,
        amount: minStake,
        minStake,
        message: taunt.trim(),
        pickLabel,
      });
      onClose?.();
    } catch {
      // Toast already shown from socket handlers.
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="bc-challenge-root" role="presentation">
      <button
        type="button"
        className="bc-modal-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="bc-challenge-sheet" role="dialog" aria-modal="true">
        <div className="bc-challenge-head">
          <span className="bc-challenge-icon" aria-hidden>
            <Zap size={16} />
          </span>
          <div>
            <p className="bc-challenge-eyebrow">Create challenge</p>
            <h3 className="bc-challenge-title">Set the terms clearly</h3>
          </div>
          <button
            type="button"
            className="bc-challenge-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="bc-challenge-scroll">
          <FixtureTagChips refs={[fixtureRef]} variant="composer" />

          <section className="bc-challenge-step">
            <StepHeading
              number="1"
              title="Market and outcome"
              hint="Pick the outcome, then whether you're for or against it"
            />
            <div className="bc-challenge-stance">
              <button
                type="button"
                className={`bc-challenge-stance-for${
                  stance === "for" ? " is-active" : ""
                }`}
                onClick={() => setStance("for")}
              >
                For (happens)
              </button>
              <button
                type="button"
                className={`bc-challenge-stance-against${
                  stance === "against" ? " is-active" : ""
                }`}
                onClick={() => setStance("against")}
              >
                Against (doesn't)
              </button>
            </div>
            <div className="bc-challenge-market-rail">
              {markets.map((market) => {
                const active = market.id === selectedMarket?.id;
                return (
                  <button
                    key={market.id}
                    type="button"
                    className={`bc-challenge-market${active ? " is-active" : ""}`}
                    onClick={() => {
                      setSelectedMarketId(market.id);
                      setSelectedPick(null);
                    }}
                  >
                    <strong>{market.title}</strong>
                    <small>{market.subtitle}</small>
                  </button>
                );
              })}
            </div>
            <div className="bc-challenge-outcome-head">
              <p className="bc-challenge-group-label">{selectedMarket?.title}</p>
              <small>{selectedMarket?.outcomes.length || 0} outcomes</small>
            </div>
            <div className="bc-challenge-outcome-rail">
              {selectedMarket?.outcomes.map(renderOutcome)}
            </div>
          </section>

          <section className="bc-challenge-step">
            <StepHeading
              number="2"
              title="Your budget"
              hint="Total Cash you lock upfront — max loss if counters win"
            />
            <label className="bc-challenge-amount">
              <span>CASH</span>
              <input
                inputMode="numeric"
                value={budgetText}
                onChange={(event) => updateBudget(event.target.value)}
                placeholder="Minimum 500"
                aria-label="Challenge creator budget"
              />
            </label>
            {!budgetValid && budgetText && (
              <p className="bc-challenge-validation">
                Minimum challenge budget is 500 CASH
              </p>
            )}
            <div className="bc-challenge-presets">
              {CHALLENGE_STAKES.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={budget === value ? "is-active" : ""}
                  onClick={() => setBudgetText(String(value))}
                >
                  {value.toLocaleString()}
                </button>
              ))}
            </div>
          </section>

          <section className="bc-challenge-step">
            <StepHeading
              number="3"
              title="Offered odds"
              hint="Decimal return for counters (stake × odds = payout)"
            />
            <label className="bc-challenge-amount">
              <input
                inputMode="decimal"
                value={oddsText}
                onChange={(event) =>
                  setOddsText(event.target.value.replace(/[^0-9.]/g, ""))
                }
                placeholder="2.00"
                aria-label="Offered odds"
              />
            </label>
            {!oddsValid && oddsText && (
              <p className="bc-challenge-validation">Odds must be 1.01–50</p>
            )}
            <div className="bc-challenge-presets">
              {CHALLENGE_ODDS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={oddsText === value ? "is-active" : ""}
                  onClick={() => setOddsText(value)}
                >
                  {Number(value).toFixed(2)}
                </button>
              ))}
            </div>
          </section>

          <section className="bc-challenge-step">
            <StepHeading
              number="4"
              title="Min counter stake"
              hint={`Floor Cash ${MIN_CHALLENGE_STAKE} · must fit your budget at these odds`}
            />
            <label className="bc-challenge-amount">
              <span>CASH</span>
              <input
                inputMode="numeric"
                value={minStakeText}
                onChange={(event) => updateMinStake(event.target.value)}
                placeholder={String(MIN_CHALLENGE_STAKE)}
                aria-label="Minimum counter stake"
              />
            </label>
            {!minStakeValid && minStakeText && (
              <p className="bc-challenge-validation">
                {capacity < MIN_CHALLENGE_STAKE
                  ? `Budget too small at these odds — need at least ${Math.ceil(
                      MIN_CHALLENGE_STAKE * (offeredOdds - 1)
                    )} CASH`
                  : minStake < MIN_CHALLENGE_STAKE
                    ? `Minimum stake is ${MIN_CHALLENGE_STAKE} CASH`
                    : `Min stake would cost ${Math.round(
                        minStake * (offeredOdds - 1)
                      )} CASH from your budget (max affordable ${capacity.toLocaleString()} CASH)`}
              </p>
            )}
            <div className="bc-challenge-presets">
              {CHALLENGE_STAKES.map((value) => {
                const affordable =
                  capacity >= MIN_CHALLENGE_STAKE && value <= capacity;
                return (
                  <button
                    key={`min-${value}`}
                    type="button"
                    className={minStake === value ? "is-active" : ""}
                    disabled={!affordable}
                    onClick={() => affordable && setMinStakeText(String(value))}
                  >
                    {value.toLocaleString()}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="bc-challenge-summary">
            <Wallet size={16} />
            <span>
              <small>You lock upfront</small>
              <strong>{budgetValid ? budget.toLocaleString() : "—"} CASH</strong>
            </span>
            <em>
              {[
                oddsValid ? `@ ${offeredOdds}` : null,
                minStakeValid ? `min ${minStake.toLocaleString()}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </em>
          </div>

          <textarea
            className="bc-composer-input bc-challenge-taunt"
            value={taunt}
            onChange={(event) => setTaunt(event.target.value)}
            placeholder="Add a message (optional)"
            rows={2}
            maxLength={280}
          />
        </div>

        <button
          type="button"
          className="bc-challenge-drop"
          disabled={!canSubmit}
          onClick={handleDrop}
        >
          {submitting ? <Loader2 size={15} className="bc-spin" /> : <Zap size={15} />}
          {submitting ? "Creating…" : "Post challenge"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ChallengeDropSheet;
